// Vendor
import { route } from 'quasar/wrappers';
import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router';

// Stores
import { useRouterStore } from '@stores/router';

// Personal libs
import { RouteEnum, RouteEnumTools } from '@shared/enum/route.enum';
import routes from './routes';
import { RoleEnum } from '@shared/enum/role.enum';
import bus from '@shared/lib/eventbus.lib';
import { BusEventDialogEnum } from '@shared/enum/bus/bus-events-dialog.enum';
import { BusEventEnum } from '@shared/enum/bus/bus-events.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory;

  const Router = createRouter({
    routes,
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        bus.emit(BusEventDialogEnum.CLOSE_LAST_MODAL);
        return savedPosition;
      } else {
        // Simulate back/forward button
        return { letf: 0, top: 0 };
      }
    },

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(
      process.env.MODE === 'ssr' ? void 0 : process.env.VUE_ROUTER_BASE,
    ),
  });

  /*
   *
   * This will check to see if the user is authenticated or not
   *
   */
  Router.beforeEach(async (to, from, next) => {
    // // https://www.digitalocean.com/community/tutorials/vuejs-vue-router-modify-head
    // // This goes through the matched routes from last to first, finding the closest route with a title.
    // // eg. if we have /some/deep/nested/route and /some, /deep, and /nested have titles, nested's will be chosen.
    // const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title)

    // Stores
    const routerStore = useRouterStore();

    if (routerStore.modalsOpened.length > 0) {
      routerStore.stopRouterNavigation = true;

      // Manage back button : close last opened modal
      bus.emit(BusEventDialogEnum.CLOSE_LAST_MODAL);

      next(from);
    } else {
      routerStore.stopRouterNavigation = false;

      // Get currentUser
      if (!sessionManager.currentUser.value) {
        // await userInfoUpdateService.exec()
      }
      const user = sessionManager.currentUser.value;

      // Then: check permissions
      const requiredPermissions = (to.meta.requiredPermissions ?? []) as Array<RoleEnum>
      const isLocalRole = requiredPermissions.some(p => p === RoleEnum.ROLE_LOCAL)
      const authorized = requiredPermissions.length === 0 || (
        user?.roles != null && requiredPermissions.some(p => user.roles?.includes(p))
      )

      if (!authorized && !isLocalRole) {
        next(`/${RouteEnum.HOME}`);
      } else {
        next();
      }
    }
  });

  /*
   *
   * This will update the activeRoute
   *
   */
  Router.afterEach((to) => {
    // Stores
    const routerStore = useRouterStore();

    routerStore.activeRoute = RouteEnumTools.convertPathToRoute(to.path);

    if (!routerStore.stopRouterNavigation) {
      bus.emit(BusEventEnum.DO_FOCUS_ON_MAIN);
    }
  });

  return Router;
});
